import { GridApi } from 'ag-grid-community';
import { ChangeEvent } from 'react';
import { UseQueryResult } from 'react-query';
import { CashInBankAccountsType } from 'components/CashInBankAccountsDropdown/types';
import { DropdownOption, DropdownValue } from 'components/Dropdown/types';
import { VendorsTableDataType } from 'pages/VendorsTablePage/components/VendorsTable/types';

export type InvoiceDataType = {
  vendorNo: string;
  vendorName: string;
  paymentMethod: string;
  invoices: InvoiceDetails[];
  totalOwed: string;
  totalDiscountAmount: string;
  totalVendorOwedAmount: string;
  totalInvoices: number;
};

export type InvoiceDetails = {
  invoiceId: string;
  invoiceDate: string;
  dueDate: string;
  amount: string;
  discountAmount: string;
  netAmount: string;
  onHold: boolean;
  invoiceDescription: string;
};

export type AuthorizePaymentPageState = {
  authorizePaymentReview: AuthorizePaymentReviewType;
  selectedInvoices: InvoiceDataType[];
  gridApi: GridApi<VendorsTableDataType> | null;
  updatedInvoiceDescriptions: UpdateInvoiceDescriptionParams[];
  shouldUpdateInvoiceDescription: boolean;
};

export type AuthorizePaymentReviewType = {
  paymentDate: string;
  cashInBankAccount?: string;
  accountingDate?: string;
  postingDescription: string;
  authorizationPaymentList: AuthorizationPaymentListItem[];
  companyId?: string;
};

export type AuthorizationPaymentListItem = {
  vendorNo: string;
  vendorName: string;
  invoiceId: string;
  amount: number;
  invoiceDescription: string;
  invoiceDate?: string;
  dueDate?: string;
  discountAmount: number;
  netAmount: number;
  onHold?: boolean;
};

export type AuthorizationPaymentRecordType = {
  vendorNo: string;
  vendorName: string;
  invoiceId: string;
  amount: string;
  invoiceDescription: string;
  invoiceDate?: string;
  dueDate?: string;
  discountAmount: string;
  netAmount: string;
  onHold?: boolean;
  paymentMethod?: string;
};

export type InterCompanyAuthorizationPaymentRecordType = {
  vendorNo: string;
  vendorName: string;
  invoiceId: string;
  amount: string;
  invoiceDescription: string;
  invoiceDate?: string;
  dueDate?: string;
  discountAmount: string;
  netAmount: string;
  onHold?: boolean;
};

export type InvoiceRowItemDataType = {
  data: AuthorizationPaymentRecordType;
};

export type LinkIconPositions = {
  TRAILING: string;
};

export enum LockStatus {
  LOCKED = 'LOCKED',
  UNLOCKED = 'UNLOCKED'
}

export type LockInvoicePayload = {
  lockStatus: LockStatus;
  invoices: AuthorizePaymentInvoice[];
};

export type LockInvoiceSuccess = {
  readonly lockStatus: LockStatus;
} & AuthorizePaymentInvoice;

export type LockInvoiceFailure = {
  readonly vendorNo: string;
  readonly invoiceId: string;
  readonly errorCode: number | null;
  readonly errorMsg: string | null;
};

export type LockInvoiceResponse = {
  readonly successes: LockInvoiceSuccess[];
  readonly failures: LockInvoiceFailure[];
};

export type InvoiceData = {
  id: number;
  data: AuthorizationPaymentRecordType[] | undefined;
  tableHeaderTitle: string;
};

export type InvoiceTableProps = {
  onRowChange: (
    formattedData: AuthorizationPaymentRecordType,
    eve: ChangeEvent<HTMLInputElement>
  ) => void;
  gridApiParams: (gridApi: GridApi) => void;
};

export type GridParams = {
  data: AuthorizationPaymentRecordType;
  api: GridApi;
};

export type AuthorizePaymentHeaderProps = {
  handleAuthorizePayment: () => void;
  disableAuthButton?: boolean;
  isLoading: boolean;
};

export type UpdateInvoiceDescriptionParams = {
  vendorNo: string;
  invoiceId: string;
  description: string;
};

export type InputSizes = {
  LARGE: string;
  SMALL: string;
  STANDARD: string;
};

export type PaymentApprovalInvoice = {
  invoiceId: string;
};

export type PaymentApproval = {
  vendorId: string;
  paymentType: string;
  isPrintApproved: boolean;
  invoices: PaymentApprovalInvoice[];
};

export type PaymentApprovalPayload = PaymentApproval[];

export type AuthorizePaymentInvoice = {
  vendorNo: string;
  invoiceId: string;
};

export type AuthorizePaymentPayload = AuthorizePaymentInvoice[];

export type InvoiceDescriptionUpdate = {
  invoiceId: string;
  invoiceDescription: string;
};

export type UpdateInvoiceDescriptionVendor = {
  vendorId: string;
  invoices: InvoiceDescriptionUpdate[];
};

export type UpdateInvoiceDescriptionPayload = UpdateInvoiceDescriptionVendor[];

export type CIBDropdownResult = {
  cashInBankAccountsOptions: DropdownValue[] | undefined;
} & UseQueryResult<CashInBankAccountsType[]>;

export type JPMCFieldsType = {
  selectedCIBAccount?: string;
  paymentDate?: string;
  selectedCashCompany?: DropdownOption;
  selectedCashInBank?: DropdownOption;
  selectedJournal?: DropdownOption;
  postingDescription?: string;
};

export type EditButtonType = {
  isEdit: boolean;
  setIsEdit: (isEdit: boolean) => void;
};

export type ResetButtonType = {
  setReset: () => void;
  isEdit: boolean;
  setIsEdit: (isEdit: boolean) => void;
};
